import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import MotionSection from '../components/ui/MotionSection';
import Spotlight from '../components/ui/Spotlight';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiArrowRight, FiCheck } = FiIcons;

const WatchDemo = () => {
  const features = [
    "See AI generate personalized cold emails in real-time",
    "Learn how to use different frameworks (AIDA, PAS, BAB)",
    "Watch A/B testing and variant creation",
    "Discover export and CRM integration features",
    "Get tips for maximum conversion rates"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20"
    >
      {/* Hero Section */}
      <MotionSection className="relative py-20 overflow-hidden">
        <Spotlight size={1000} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Watch ColdMail AI <span className="gradient-text">in action</span>
          </h1>
          <p className="text-xl text-light/80 mb-8 max-w-3xl mx-auto">
            See how our AI transforms simple inputs into high-converting cold emails in seconds
          </p>
        </div>
      </MotionSection>

      {/* Video Section */}
      <MotionSection className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Video */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="gradient-border rounded-xl p-4">
                <div className="gradient-border-inner p-4 bg-black rounded-lg">
                  <div className="aspect-[9/16] w-full max-w-[315px] mx-auto">
                    <iframe 
                      width="100%" 
                      height="100%" 
                      src="https://www.youtube.com/embed/v6xMUTUP-q0" 
                      title="AI Email Generator Pro" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      referrerPolicy="strict-origin-when-cross-origin" 
                      allowFullScreen
                      className="rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  What you'll learn in this <span className="gradient-text">demo</span>
                </h2>
                <p className="text-lg text-light/70 mb-8">
                  This comprehensive walkthrough shows you exactly how to generate 
                  high-converting cold emails using our AI-powered platform.
                </p>
              </div>

              <div className="space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3"
                  >
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-light/80">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Link
                  to="/app"
                  className="btn-primary px-8 py-4 rounded-lg text-white font-medium text-center flex items-center justify-center space-x-2"
                >
                  <span>Try It Free Now</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="px-8 py-4 border border-primary text-primary rounded-lg font-medium text-center hover:bg-primary hover:text-white transition-colors"
                >
                  View Pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </MotionSection>

      {/* Stats Section */}
      <MotionSection className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Join thousands of <span className="gradient-text">successful</span> users
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-primary mb-2">340%</div>
              <div className="text-light/60">Average response rate increase</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-primary mb-2">10,000+</div>
              <div className="text-light/60">Emails generated daily</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-primary mb-2">95%</div>
              <div className="text-light/60">Time saved on email writing</div>
            </motion.div>
          </div>
        </div>
      </MotionSection>

      {/* CTA Section */}
      <MotionSection className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="gradient-border rounded-2xl p-8">
            <div className="gradient-border-inner p-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to <span className="gradient-text">transform</span> your outreach?
              </h2>
              <p className="text-xl text-light/80 mb-8">
                Start generating high-converting cold emails in seconds. No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/app"
                  className="btn-primary px-8 py-4 rounded-lg text-white font-medium text-lg"
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/pricing"
                  className="px-8 py-4 border border-primary text-primary rounded-lg font-medium text-lg hover:bg-primary hover:text-white transition-colors"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </MotionSection>
    </motion.div>
  );
};

export default WatchDemo;